/**
 * TopNav — per design-contract §7 + cycle 2 brief §3 stage 3.
 *
 * Mode layout:
 *  tier-0:       wordmark | Dashboard | Marketplace | [avatar if authed]
 *  reference:    wordmark only (no nav items, no auth chrome)
 *  vertical-ref: wordmark | Dashboard | Marketplace | Administration | OrgSwitcher | avatar
 *  tier-1-hub:   wordmark | Dashboard | Marketplace | Administration | OrgSwitcher | avatar
 *
 * Domain-agnosticism: no hostname strings. All navigation is hash-based or
 * derived from window.location.origin via keycloak logout.
 */

import { useState, useRef, useEffect } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import { t } from '../i18n';
import type { Route } from '../shell/Router';
import Wordmark from './Wordmark';
import OrgSwitcher from './OrgSwitcher';

interface TopNavProps {
  activePath: Route;
  onNavigate: (path: Route) => void;
}

// ── Avatar + dropdown ─────────────────────────────────────────────────────────

function Avatar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() ||
      user.email.charAt(0).toUpperCase()
    : '?';

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function keyHandler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={open}
        aria-haspopup="menu"
        style={{
          width: 32,
          height: 32,
          borderRadius: 'var(--radius-pill)',
          background: 'var(--dp-navy-600)',
          color: 'var(--fg-on-navy)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-meta)',
          fontWeight: 600,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          letterSpacing: '0.04em',
        }}
      >
        {initials}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + var(--sp-2))',
            right: 0,
            minWidth: '180px',
            background: 'var(--bg-surface)',
            boxShadow: 'var(--shadow-3)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            zIndex: 200,
          }}
        >
          <div
            style={{
              padding: 'var(--sp-3) var(--sp-4)',
              borderBottomWidth: '1px',
              borderBottomStyle: 'solid',
              borderBottomColor: 'var(--border-subtle)',
            }}
          >
            <p
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-body-sm)',
                fontWeight: 500,
                color: 'var(--fg-1)',
                margin: 0,
              }}
            >
              {user?.firstName} {user?.lastName}
            </p>
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--fs-meta)',
                color: 'var(--fg-3)',
                margin: '2px 0 0',
              }}
            >
              {user?.email}
            </p>
          </div>

          <button
            role="menuitem"
            onClick={() => setOpen(false)}
            style={{
              width: '100%',
              padding: 'var(--sp-2) var(--sp-4)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              color: 'var(--fg-2)',
              display: 'block',
            }}
          >
            {t('nav.profile')}
          </button>

          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            style={{
              width: '100%',
              padding: 'var(--sp-2) var(--sp-4)',
              background: 'transparent',
              border: 'none',
              borderTopWidth: '1px',
              borderTopStyle: 'solid',
              borderTopColor: 'var(--border-subtle)',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body-sm)',
              color: 'var(--fg-2)',
              display: 'block',
            }}
          >
            {t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

// ── TopNav ────────────────────────────────────────────────────────────────────

export default function TopNav({ activePath, onNavigate }: TopNavProps) {
  const config = useConfig();
  const mode = config.instance.mode;

  // reference mode: wordmark only, no nav, no auth chrome (contract §7)
  if (mode === 'reference') {
    return (
      <nav className="dp-shell__nav" aria-label="Main navigation">
        <Wordmark />
      </nav>
    );
  }

  const isHubLike = mode === 'vertical-ref' || mode === 'tier-1-hub';

  const navItems: Array<{ label: string; path: Route }> =
    mode === 'tier-0'
      ? [
          { label: t('nav.dashboard'), path: '/dashboard' },
          { label: t('nav.marketplace'), path: '/' },
        ]
      : isHubLike
      ? [
          { label: t('nav.dashboard'), path: '/dashboard' },
          { label: t('nav.marketplace'), path: '/' },
          { label: t('nav.administration'), path: '/admin' },
        ]
      : [];

  return (
    <nav className="dp-shell__nav" aria-label="Main navigation">
      <Wordmark />

      {navItems.length > 0 && (
        <ul
          role="list"
          style={{
            display: 'flex',
            gap: 'var(--sp-6)',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            flex: 1,
          }}
        >
          {navItems.map(({ label, path }) => {
            const isActive =
              path === '/admin'
                ? activePath === '/admin' || activePath === '/admin/sub-clients'
                : activePath === path;
            return (
              <li key={path}>
                <button
                  onClick={() => onNavigate(path)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-body-sm)',
                    fontWeight: 500,
                    color: isActive ? 'var(--fg-on-navy)' : 'rgba(250,247,242,0.65)',
                    padding: '0 0 2px',
                    borderBottom: isActive
                      ? '2px solid var(--dp-red-500)'
                      : '2px solid transparent',
                    transition: 'color var(--dur-fast) var(--ease-out)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLButtonElement).style.color =
                        'var(--fg-on-navy)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      (e.currentTarget as HTMLButtonElement).style.color =
                        'rgba(250,247,242,0.65)';
                  }}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Right-hand cluster: OrgSwitcher + Avatar for hub-like modes */}
      {isHubLike && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-3)',
            marginLeft: 'auto',
          }}
        >
          <OrgSwitcher />
          <Avatar />
        </div>
      )}
    </nav>
  );
}
